import axios from "axios";

import RequestToken from "../utils/requestToken";

// Create axios instance with default config
/**
 * Axios instance pre-configured for API requests.
 *
 * This instance is set up with the following configurations:
 * - The base URL for all requests is derived from the environment variable `VITE_API_URL`.
 *   If the variable is not defined, it defaults to `/api`.
 * - A timeout of 60000 milliseconds (1 minute) is enforced for all requests.
 * - Default headers include `Content-Type: application/json`.
 *
 * This instance is intended for making HTTP requests to the application's backend API.
 */
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  timeout: 60000, // 1 minute timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    if (RequestToken.token) {
      config.headers.Authorization = `Bearer ${RequestToken.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Server responded with error status
      console.error("API Error:", error.response.data);
    } else if (error.request) {
      // Request was made but no response
      console.error("No response from server");
    } else {
      // Something else happened
      console.error("Error:", error.message);
    }
    return Promise.reject(error);
  },
);

// API endpoints
/**
 * A service object providing API methods for handling file uploads, downloads,
 * validation processes, data export, user and vendor lookups, Google Sheets operations,
 * mail merges, and gift selection features.
 */
const apiService = {
  // Upload endpoints
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  downloadTemplate: () => {
    return api.get("/template", {
      responseType: "blob",
    });
  },

  // Validation endpoints
  getValidationProgress: (jobId) => {
    return api.get(`/validate/${jobId}`);
  },

  revalidateAddresses: (jobId, rows) => {
    return api.post("/revalidate", {
      job_id: jobId,
      rows,
    });
  },

  skipInvalidAddresses: (jobId, rowNumbers) => {
    return api.post(`/skip/${jobId}`, rowNumbers);
  },

  getValidationResults: (jobId) => {
    return api.get(`/results/${jobId}`);
  },

  // Export endpoints
  downloadValidatedData: (jobId, format = "xlsx") => {
    return api.get(`/download/${jobId}`, {
      params: { format },
      responseType: "blob",
    });
  },

  exportValidAddresses: (jobId, format = "xlsx") => {
    return api.get(`/export/valid/${jobId}`, {
      params: { format },
      responseType: "blob",
    });
  },

  // Lookup endpoints
  lookupUser: (email) => {
    return api.post("/lookup/user", { email });
  },

  lookupVendor: (thnksId) => {
    return api.post("/lookup/vendor", { thnks_id: thnksId });
  },

  verifyDatabaseConnection: () => {
    return api.get("/lookup/verify");
  },

  // Google Sheets endpoints
  postToGoogleSheets: (jobId, tabName = null) => {
    return api.post(`/post-to-sheets/${jobId}`, {
      tab_name: tabName,
    });
  },

  // Mail merge endpoints
  downloadMailMerge: (jobId) => {
    return api.get(`/mail-merge/${jobId}`, {
      responseType: "blob",
    });
  },

  // Thnks selection endpoints
  getAvailableGifts: () => {
    return api.get("/gifts");
  },

  getGiftBehaviors: () => {
    return api.get("/lookup/gift-behaviors");
  },

  getMessageTemplates: (giftId = null) => {
    const params = giftId ? { gift_id: giftId } : {};
    return api.get("/message-templates", { params });
  },

  applyThnksSelection: (jobId, selections) => {
    return api.post(`/apply-thnks/${jobId}`, selections);
  },
};

export default apiService;
